import { useEffect, useRef } from "react";

export type DebouncedEffectOpts = {
  signal: AbortSignal;
  requestId: number;
};

/**
 * Debounced + abortable effect hook.
 *
 * effect: called after debounce delay with { signal, requestId }.
 *   - effect may return a Promise; any thrown error is logged (AbortError is ignored).
 * deps: dependencies to watch (same semantics as useEffect deps)
 * delay: debounce delay in ms (default 500)
 *
 * IMPORTANT: prefer passing a stable `effect` (wrap with useCallback) to avoid extra re-runs.
 */
export function useDebouncedAbortableEffect(
  effect: (opts: DebouncedEffectOpts) => void | Promise<void>,
  deps: React.DependencyList = [],
  delay: number = 500
): void {
  const controllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef<boolean>(false);
  const latestRequestIdRef = useRef<number>(0);

  // track mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // On each deps change: clear scheduled timer and abort previous running request
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    const requestId = ++latestRequestIdRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;

    timerRef.current = window.setTimeout(() => {
      // call user's effect with { signal, requestId }
      try {
        const maybePromise = effect({ signal: controller.signal, requestId });

        if (maybePromise && typeof (maybePromise as Promise<void>).then === "function") {
          (maybePromise as Promise<void>).catch((err: any) => {
            if (err && err.name === "AbortError") {
              // ignore intentional aborts
              return;
            }
            // surface/log unexpected error
            // Replace with your logging mechanism (Sentry, etc.)
            // eslint-disable-next-line no-console
            console.error("useDebouncedAbortableEffect error:", err);
          });
        }
      } catch (err: any) {
        if (err && err.name === "AbortError") {
          return;
        }
        // eslint-disable-next-line no-console
        console.error("useDebouncedAbortableEffect synchronous error:", err);
      } finally {
        timerRef.current = null;
      }
    }, delay);

    // cleanup for this run (on deps change)
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
    // Note: user should pass stable `effect` (useCallback) or accept re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
